import React, { useEffect } from 'react';
import { useTranslation } from '../contexts/TranslationContext';
import { InlineWidget } from "react-calendly";

const Contact = ({ onMeetingBooked }) => {
  const { t } = useTranslation();

  useEffect(() => {
    // Listen for Calendly events via window messages (more reliable)
    const handleCalendlyMessage = (event) => {
      if (event.origin !== 'https://calendly.com') return;
      
      // Convert entire message to string for text-based detection
      const messageStr = JSON.stringify(event.data).toLowerCase();
      
      // Check for booking-related keywords in the entire message
      if (messageStr.includes('scheduled') || 
          messageStr.includes('booked') || 
          messageStr.includes('confirmed') ||
          messageStr.includes('invitation') ||
          messageStr.includes('success')) {
      }
      
      // Check for various possible booking-related events
      if (event.data && event.data.event) {
        const eventType = event.data.event;
        
        // Try multiple possible event names for booking
        if (eventType === 'calendly.event_scheduled' || 
            eventType === 'calendly.event_type_scheduled' ||
            eventType === 'calendly.booking_completed' ||
            eventType === 'calendly.invitee_scheduled' ||
            eventType.includes('scheduled') ||
            eventType.includes('booked') ||
            eventType.includes('confirmed')) {
          // Clear the "seen" flag since this is a fresh booking
          localStorage.removeItem('noctrifyPromoSeen');
          if (onMeetingBooked) {
            onMeetingBooked();
          }
        }
      }
      
      // Also check if this is a profile view event that might indicate completion
      if (event.data && event.data.event === 'calendly.profile_page_viewed') {
        // If we see a profile page view, check if it's the success/confirmation page
        setTimeout(() => {
          // You could add additional logic here to check page state
        }, 1000);
      }
    };

    // Also watch for DOM changes that indicate booking success
    const watchForBookingSuccess = () => {
      const checkForSuccess = () => {
        // Look for success indicators in the iframe content
        const calendlyContainer = document.querySelector('.calendly-container');
        if (calendlyContainer) {
          const iframe = calendlyContainer.querySelector('iframe');
          if (iframe) {
            try {
              // Look for success messages or confirmation elements
              const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
              const successElements = iframeDoc.querySelectorAll('*');
              for (let element of successElements) {
                const text = element.textContent || '';
                if (text.includes('You are scheduled') || 
                    text.includes('scheduled') || 
                    text.includes('confirmation') ||
                    text.includes('calendar invitation')) {
                  if (onMeetingBooked) {
                    onMeetingBooked();
                  }
                  return;
                }
              }
            } catch (e) {
            }
          }
        }
      };

      // Check periodically for booking success
      const interval = setInterval(checkForSuccess, 2000);
      setTimeout(() => clearInterval(interval), 60000); // Stop after 1 minute
    };

    window.addEventListener('message', handleCalendlyMessage);
    watchForBookingSuccess();
    
    return () => {
      window.removeEventListener('message', handleCalendlyMessage);
    };
  }, [onMeetingBooked]);

  const handleEventScheduled = (event) => {
    // Backup method: direct callback
    if (onMeetingBooked) {
      onMeetingBooked();
    }
  };

  // Debug function to test the popup
  const testPopup = () => {
    // Clear the "seen" flag so popup can show again
    localStorage.removeItem('noctrifyPromoSeen');
    if (onMeetingBooked) {
      onMeetingBooked();
    }
  };

  return (
    <section id="contact" className="contact">
      <div className="container">
        <div className="section-header">
          <h2>{t('contact-ready-title')}</h2>
          <p>{t('contact-ready-subtitle')}</p>
        </div>
        <div className="contact-content">
          <div className="consultation-booking">
            <div className="booking-card">
              <h3>{t('schedule-consultation-title')}</h3>
              <p className="booking-subtitle">{t('schedule-consultation-subtitle')}</p>
              

              {/* Debug Buttons - Remove after testing */}
              <div style={{ marginBottom: '15px', textAlign: 'center', gap: '10px', display: 'flex', justifyContent: 'center', flexWrap: 'wrap' }}>
                <button 
                  onClick={testPopup}
                  style={{
                    display: 'none'
                  }}
                >
                  🐛 Test Popup
                </button>
                <button 
                  onClick={() => {
                    localStorage.removeItem('noctrifyPromoSeen');
                    if (onMeetingBooked) {
                      onMeetingBooked();
                    }
                  }}
                  style={{
                    display: 'none'
                  }}
                >
                  ✅ I just booked - Show popup!
                </button>
              </div>

              {/* Calendly Widget */}
              <div className="calendly-container">
                <InlineWidget 
                  url="https://calendly.com/info-itb-itb-solution/30min"
                  styles={{
                    height: '600px',
                    width: '100%'
                  }}
                  onEventScheduled={handleEventScheduled}
                />
              </div>
              
              <div className="booking-footer">
                <p className="booking-note">Having trouble with the scheduler?</p>
                <a href="https://calendly.com/info-itb-itb-solution/30min" target="_blank" rel="noopener noreferrer" className="backup-contact">
                  <i className="fas fa-external-link-alt"></i>
                  <span>Open Calendly in New Tab</span>
                </a>
              </div>
            </div>
          </div>
          
          <div className="consultation-info">
            <div className="info-section">
              <div className="info-header">
                <div className="info-icon green">
                  <i className="fas fa-clipboard-check"></i>
                </div>
                <h3>{t('consultation-what-to-expect')}</h3>
              </div>
              <ul className="info-list">
                <li>
                  <i className="fas fa-check"></i>
                  <span>{t('consultation-business-analysis')}</span>
                </li>
                <li>
                  <i className="fas fa-check"></i>
                  <span>{t('consultation-jurisdiction-recommendations')}</span>
                </li>
                <li>
                  <i className="fas fa-check"></i>
                  <span>{t('consultation-custom-proposal')}</span>
                </li>
                <li>
                  <i className="fas fa-check"></i>
                  <span>{t('consultation-next-steps')}</span>
                </li>
              </ul>
            </div>
            
            <div className="info-section">
              <div className="info-header">
                <div className="info-icon blue">
                  <i className="fas fa-briefcase"></i>
                </div>
                <h3>{t('consultation-come-prepared')}</h3>
              </div>
              <ul className="info-list">
                <li>
                  <i className="fas fa-circle"></i>
                  <span>{t('consultation-business-goals')}</span>
                </li>
                <li>
                  <i className="fas fa-circle"></i>
                  <span>{t('consultation-preferred-jurisdiction')}</span>
                </li>
                <li>
                  <i className="fas fa-circle"></i>
                  <span>{t('consultation-required-features')}</span>
                </li>
                <li>
                  <i className="fas fa-circle"></i>
                  <span>{t('consultation-timeline-budget')}</span>
                </li>
              </ul>
            </div>
            
            <div className="guarantee-badge">
              <div className="badge-icon">
                <i className="fas fa-shield-check"></i>
              </div>
              <div className="badge-text">
                <strong>{t('consultation-guarantee')}</strong>
              </div>
            </div>
            
            <div className="alternative-contact">
              <h4>{t('consultation-alternative-contact')}</h4>
              <div className="contact-methods">
                <div className="contact-method">
                  <div className="method-icon">
                    <i className="fas fa-envelope"></i>
                  </div>
                  <div className="method-info">
                    <h5>{t('consultation-email-us')}</h5>
                    <p>info.itb@itb-solution.com</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div className="trust-indicators">
          <div className="trust-item">
            <div className="trust-icon">
              <i className="fas fa-clock"></i>
            </div>
            <h4>{t('trust-response-time')}</h4>
            <p>{t('trust-response-desc')}</p>
          </div>
          <div className="trust-item">
            <div className="trust-icon">
              <i className="fas fa-handshake"></i>
            </div>
            <h4>{t('trust-no-pressure')}</h4>
            <p>{t('trust-no-pressure-desc')}</p>
          </div>
          <div className="trust-item">
            <div className="trust-icon">
              <i className="fas fa-award"></i>
            </div>
            <h4>{t('trust-professional')}</h4>
            <p>{t('trust-professional-desc')}</p>
          </div>
          <div className="trust-item">
            <div className="trust-icon">
              <i className="fas fa-lock"></i>
            </div>
            <h4>{t('trust-confidential')}</h4>
            <p>{t('trust-confidential-desc')}</p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact; 